import { ApiError } from '@/lib/api'
import { paymentsApi, isOnline, type PayRentBody, type PayRentResponse } from '@/features/payments/payments.api'
import { leasePaymentApi } from '@/features/lease/lease-payment.api'
import { agreementPaymentApi } from '@/features/agreement/agreement-payment.api'
import { subscriptionApi } from '@/features/subscription/subscription.api'
import { useSubscriptionStore } from '@/features/subscription/subscription.store'
import { openOrderGateway, openRentGateway, openSubscriptionGateway } from '@/features/payments/gateways'
import { pollUntil } from '@/features/payments/confirm'
import { invalidateFor } from '@/lib/query-keys'
import { checkoutStrings } from '@/features/payments/strings'
import type { CheckoutFlow } from '@/features/payments/useCheckout'
import type { AgreementPaymentInit, LeasePaymentInit, SubscriptionUpgradeResponse } from '@/types/models'

/** Razorpay or Cashfree as the backend decides; 2-minute confirm window. `leaseCurrency` is what the order is in when the response does not say. */
export function rentCheckoutFlow(body: PayRentBody & { feeToken: string }, leaseCurrency?: string | null): CheckoutFlow<PayRentResponse> {
  return {
    start: () => paymentsApi.payRent(body),
    openGateway: (order) => (isOnline(order) ? openRentGateway(order, checkoutStrings.rentDescription, leaseCurrency) : Promise.resolve({ outcome: 'failed', message: 'Not an online payment' })),
    confirm: (order) => {
      const id = isOnline(order) ? order.transactionId : ''
      return { validate: () => paymentsApi.validate(id), status: () => paymentsApi.status(id), deadlineMs: 5 * 60 * 1000 }
    },
    onCancel: async (order) => {
      if (isOnline(order)) await paymentsApi.cancel(order.transactionId)
    },
    onSuccess: () => invalidateFor('payment'),
  }
}

/** Razorpay order, or a Paddle transaction where Paddle takes the fee (LP-514); DELETE with the payment token on dismiss; 5-minute confirm deadline. */
export function leaseFeeCheckoutFlow(leaseId: string, prefill?: Record<string, string>): CheckoutFlow<LeasePaymentInit> {
  return {
    start: () => leasePaymentApi.initiate(leaseId),
    openGateway: (init) => openOrderGateway(init, checkoutStrings.leaseFeeTitle, prefill),
    confirm: () => ({ validate: () => leasePaymentApi.validate(leaseId), status: () => leasePaymentApi.status(leaseId), deadlineMs: 5 * 60 * 1000 }),
    onCancel: async (init) => {
      await leasePaymentApi.cancel(leaseId, init.paymentToken)
    },
    onSuccess: () => invalidateFor('tenant', 'property', 'payment'),
  }
}

/** Razorpay order; DELETE with the payment token on dismiss; no hard deadline. */
export function agreementCheckoutFlow(agreementId: string, hardCopyRequested: boolean, prefill?: Record<string, string>): CheckoutFlow<AgreementPaymentInit> {
  return {
    start: () => agreementPaymentApi.initiate(agreementId, hardCopyRequested),
    openGateway: (init) => openOrderGateway(init, checkoutStrings.agreementDescription, prefill),
    confirm: () => ({ validate: () => agreementPaymentApi.validate(agreementId), status: () => agreementPaymentApi.status(agreementId) }),
    onCancel: async (init) => {
      await agreementPaymentApi.cancel(agreementId, init.paymentToken)
    },
    onSuccess: () => invalidateFor('agreement'),
  }
}

// Free plans skip the gateway. Polls GET /v2/subscription-status/:id every 5 s until the status leaves
// created/authenticated (validating once near the end); `active` is success.
// With a promo code, a 409 is the code refused (LP-1002): `onPromoRefused` hears its message before the checkout fails.
export function subscriptionCheckoutFlow(planId: string, promoCode?: string, onPromoRefused?: (message: string) => void): CheckoutFlow<SubscriptionUpgradeResponse> {
  return {
    start: () =>
      subscriptionApi.upgrade(planId, promoCode).catch((e: unknown) => {
        if (promoCode && e instanceof ApiError && e.status === 409) onPromoRefused?.(e.message)
        throw e
      }),
    openGateway: (res) => openSubscriptionGateway(res),
    confirm: (res) => ({
      validate: async () => {
        // A free plan is active already; a Paddle one (no key either) waits for its webhook like a Razorpay one.
        if (!res.key && res.provider !== 'paddle') return { confirmed: true, code: 'confirmed', remainingSeconds: 0 }
        const final = await pollUntil(() => subscriptionApi.pendingStatus(res.subscription_id), (s) => s.status !== 'created' && s.status !== 'authenticated', { intervalMs: 5000, maxAttempts: 120, onNearTimeout: () => void subscriptionApi.validate().catch(() => undefined) })
        if (final?.status === 'active') return { confirmed: true, code: 'confirmed', remainingSeconds: 0 }
        return { confirmed: false, code: 'timeout', remainingSeconds: 0 }
      },
      status: () => subscriptionApi.pendingStatus(res.subscription_id).then((s) => ({ confirmed: s.status === 'active' })),
    }),
    onCancel: async () => {
      await subscriptionApi.cancelPending()
    },
    onSuccess: async () => {
      await useSubscriptionStore.getState().refreshActive()
      useSubscriptionStore.setState({ pending: null })
      await invalidateFor('subscription')
    },
  }
}
